import {defineMessages, FormattedMessage} from 'react-intl';
import {ReactNode, useCallback, useMemo} from 'react';

import {Icon, Link, view} from '@sail/ui';
import {warning} from '@sail/icons/react/Icon';

import {safeWindowOpen} from '@stripe-internal/safe-links';

import {AppConfig, AppError} from '../types';

const messages = defineMessages({
  title: {
    id: 'conversations_assistant.unrecoverableErrorScreen.title',
    description: 'Title shown on the screen for an unrecoverable error',
    defaultMessage: 'Something went wrong',
  },
  sessionInvalidTitle: {
    id: 'conversations_assistant.unrecoverableErrorScreen.invalidSessionTitle',
    description:
      'Title shown on the screen for an unrecoverable error when the user session has expired',
    defaultMessage: 'Your session has expired',
  },
  description: {
    id: 'conversations_assistant.unrecoverableErrorScreen.description',
    description:
      'Additional information shown to the user for an unrecoverable error',
    defaultMessage:
      '<contact-link>Contact us</contact-link> with details about your issue.',
  },
  descriptionWithSignInSuggestion: {
    id: 'conversations_assistant.unrecoverableErrorScreen.descriptionWithSignInSuggestion',
    description:
      'Additional information shown to the user for an unrecoverable error, including a suggestion to try signing in again.',
    defaultMessage:
      'Try signing in again or <contact-link>contact us</contact-link> with details about your issue.',
  },
  descriptionWithInvalidSessionMessage: {
    id: 'conversations_assistant.unrecoverableErrorScreen.descriptionWithInvalidSessionMessage',
    description:
      'Additional information shown to the user for an unrecoverable error, including a message telling the user their session expired.',
    defaultMessage: 'Refresh the page or sign in again to continue.',
  },
});

function ContactLink({
  config,
  children,
}: {
  config: AppConfig;
  children: ReactNode;
}) {
  const {urls, token} = config;
  const emailContactUrl = useMemo(
    () => `${urls.supportSite}/contact/email?token=${token}`,
    [urls.supportSite, token],
  );

  const onContactClickDefault = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      safeWindowOpen(emailContactUrl, '_blank');
      e.preventDefault();
    },
    [emailContactUrl],
  );

  return (
    <Link type="primary" href={emailContactUrl} onClick={onContactClickDefault}>
      {children}
    </Link>
  );
}

type Props = {
  config: AppConfig;
  errorId?: string;
  error?: AppError | null;
};

export function ErrorScreen({config, errorId, error}: Props) {
  return (
    <view.div
      css={{
        width: '100%',
        height: '100%',
        textAlign: 'center',
        stack: 'y',
        paddingX: 'large',
      }}
      data-testid="error-screen"
      aria-live="assertive"
    >
      <view.div
        css={{
          stack: 'y',
          height: 'fill',
          gap: 'small',
          alignX: 'center',
          alignY: 'center',
        }}
      >
        <Icon icon={warning} size="large" />
        <view.div>
          <FormattedMessage {...messages.title} />
        </view.div>
        <view.div css={{font: 'body.small'}}>
          <FormattedMessage
            {...messages.description}
            values={{
              'contact-link': (value: ReactNode[]) => (
                <ContactLink config={config}>{value}</ContactLink>
              ),
            }}
          />
        </view.div>
      </view.div>
      {errorId || error?.errorId ? (
        <view.p css={{font: 'label.small', color: 'secondary'}}>
          {errorId || error?.errorId}
        </view.p>
      ) : null}
    </view.div>
  );
}
